import React, { useEffect, useState, useContext } from 'react';
import { PayPalButtons, PayPalScriptProvider, usePayPalScriptReducer, } from '@paypal/react-paypal-js';
import { Typography } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService, } from '@thd-nucleus/experience-context';
import { Alert } from '../../shared/alert/alert';
import { Spinner } from '../../shared/spinner/spinner';
import { usePayments } from '../../../hooks/use-payments';
import { CardType } from '../../../constants/payments';
import { paymentsApi } from '../../../api/common/payments';
import { PaypalCard } from '../paypal-card/paypal-card';
import { isProd } from '../../../utils/environments';
import { PAYPAL_LLC_CLIENT_ID, PAYPAL_PROD_CLIENT_ID, } from '../../../constants/paypal';
import { getUserId } from '../../../utils/customer';
import { TAlertStatus } from '../../../models/common/alert-messages';
import { CHANGES_SAVED_MSG, PAYPAL_ADDED_MSG, } from '../../../constants/response-success-messages';
import { setAddCardAnalytics, setEditCardAnalytics, } from '../../../utils/payments-analytics';
import { Pagination } from '../../shared/pagination/pagination';
import { getNewPageNumber, getPaymentsErrorCode, hasNoPaymentCards, } from '../../../utils/payments';
import { scrollToTop } from '../../../utils/general';
import { ERR_GENERIC, ERR_UNABLE_TO_SAVE, } from '../../../constants/response-error-messages';
import { pushErrorEvent, setOverlayEvent } from '../../../utils/analytics';
import { NoPaymentCardsMessage } from '../../shared/no-payment-cards-message/no-payment-cards-message';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { CustomerRole } from '../../../models/common/customer';
import { newrelic } from '../../../utils/newrelic';
const payPalClientId = isProd() ? PAYPAL_PROD_CLIENT_ID : PAYPAL_LLC_CLIENT_ID;
const getUserLevelPaypalAccount = (isB2B, isAdmin, paymentCards) => {
    if (!(paymentCards === null || paymentCards === void 0 ? void 0 : paymentCards.length))
        return null;
    return isB2B && isAdmin
        ? paymentCards.find((card) => card.userId === getUserId()) || null
        : paymentCards[0] || null;
};
const PayPalListContent = () => {
    const [deletedCardIndex, setDeletedCardIndex] = useState(-1);
    const hasDeleteIndex = deletedCardIndex > -1;
    const { channel } = useContext(ExperienceContext);
    const isMobile = channel === 'mobile';
    const [{ isPending }] = usePayPalScriptReducer();
    const { loading, paypalCards, getPaymentCards, isB2B, paginationInfo, alertMessage, currentPageNumber, setAlertMessage, } = usePayments();
    const { role } = useCustomerInfo();
    const isAdmin = role === CustomerRole.ADMIN;
    const isSavedPaypal = !!getUserLevelPaypalAccount(isB2B, isAdmin, paypalCards === null || paypalCards === void 0 ? void 0 : paypalCards.paymentCards);
    const isPaypalRestEnabled = !!useConfigService('isPaypalRestEnabled');
    const totalPages = Number(paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalPages) || 0;
    const resetAlert = () => {
        setDeletedCardIndex(-1);
        setAlertMessage(null);
    };
    const onActionFailure = (paypalAccountId, message) => {
        resetAlert();
        setAlertMessage({
            paymentId: paypalAccountId,
            text: message,
            alertStatus: TAlertStatus.ERROR,
        });
    };
    const onActionSuccess = (paypalAccountId, message, indexOfCard) => {
        resetAlert();
        setAlertMessage({
            paymentId: paypalAccountId,
            text: message,
            alertStatus: TAlertStatus.SUCCESS,
        });
        if (indexOfCard !== undefined) {
            setDeletedCardIndex(indexOfCard);
            const newPage = getNewPageNumber(currentPageNumber, totalPages, paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.paymentsOnCurrentPage);
            onChangePage(newPage);
            return;
        }
        onChangePage(1);
    };
    const handlePaypalInit = () => {
        setOverlayEvent('add paypal');
        return paymentsApi.paypalInit(isB2B);
    };
    const handlePaypalInitv2 = () => {
        setOverlayEvent('add paypal');
        return paymentsApi.initPayPalV2();
    };
    const onPaypalAddSuccess = (type) => {
        newrelic.addPageAction(`paypal-${type}-add`, {
            status: 'success',
        });
        resetAlert();
        setAlertMessage({
            text: PAYPAL_ADDED_MSG.message,
            alertStatus: TAlertStatus.SUCCESS,
        });
        onChangePage(1);
        setAddCardAnalytics('paypal', false, '', '');
    };
    const onPaypalEditSuccess = (paypalAccountId) => {
        newrelic.addPageAction('paypal-edit', {
            status: 'success',
        });
        resetAlert();
        setAlertMessage({
            paymentId: paypalAccountId,
            text: CHANGES_SAVED_MSG.message,
            alertStatus: TAlertStatus.SUCCESS,
        });
        onChangePage(1);
        setEditCardAnalytics('paypal', false, '', '');
    };
    const handlePaypalRestSave = (data) => {
        return new Promise((resolve, reject) => {
            // this is because Paypal does not return the orderID for REST invocations
            const orderID = 
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            data.orderID || (data === null || data === void 0 ? void 0 : data.vaultSetupToken);
            if (!orderID) {
                reject(new Error('No orderID or vaultSetupToken provided'));
                return;
            }
            resolve(Object.assign(Object.assign({}, data), { orderID, payerID: data.payerID }));
        })
            .then(({ orderID, payerID }) => {
            return paymentsApi.savePayPalV2({
                orderID,
                payerID,
            });
        })
            .then(() => onPaypalAddSuccess('rest'))
            .catch((error) => {
            setOverlayEvent('add paypal error');
            const errorCode = getPaymentsErrorCode(error);
            newrelic.addPageAction('paypal-rest-add', {
                status: 'failure',
                error: errorCode || error.message || 'unknown error',
            });
            onActionFailure('', ERR_UNABLE_TO_SAVE.message);
            pushErrorEvent('add paypal card', 'inline', errorCode || 'n/a', ERR_UNABLE_TO_SAVE.message);
        });
    };
    const handlePaypalSoapSave = (data) => {
        return paymentsApi
            .paypalSave({ orderID: data.orderID, payerID: data.payerID }, isB2B)
            .then(() => onPaypalAddSuccess('soap'))
            .catch((error) => {
            const errorCode = getPaymentsErrorCode(error);
            newrelic.addPageAction('paypal-soap-add', {
                status: 'failure',
                error: errorCode || error.message || 'unknown error',
            });
            onActionFailure('', ERR_UNABLE_TO_SAVE.message);
            pushErrorEvent('add paypal card', 'inline', errorCode || 'n/a', ERR_UNABLE_TO_SAVE.message);
        });
    };
    const handlePaypalEdit = ({ orderID, payerID }, paymentCard) => {
        return paymentsApi
            .paypalSave({ orderID, payerID, paymentCard }, isB2B)
            .then(() => {
            onPaypalEditSuccess(paymentCard === null || paymentCard === void 0 ? void 0 : paymentCard.paypalAccountId);
        })
            .catch((error) => {
            const errorCode = getPaymentsErrorCode(error);
            newrelic.addPageAction('paypal-edit', {
                status: 'failure',
                error: errorCode || error.message || 'unknown error',
            });
            onActionFailure((paymentCard === null || paymentCard === void 0 ? void 0 : paymentCard.paypalAccountId) || '', ERR_UNABLE_TO_SAVE.message);
            pushErrorEvent('edit paypal card', 'inline', errorCode || 'n/a', ERR_UNABLE_TO_SAVE.message);
        });
    };
    const handlePaypalError = (paypalAccountId) => {
        onActionFailure(paypalAccountId, ERR_GENERIC.message);
    };
    const onChangePage = (newPageNumber) => {
        triggerGetPayments(newPageNumber);
    };
    const triggerGetPayments = (pageNumber) => {
        getPaymentCards(CardType.PAYPAL_ACCOUNT, false, false, {
            page: pageNumber,
        });
    };
    const alert = alertMessage && (React.createElement(Alert, { key: "alert", status: alertMessage.alertStatus, onClose: resetAlert }, alertMessage.text));
    const paypalRestEnabled = (React.createElement(PayPalButtons, { fundingSource: "paypal", disabled: isSavedPaypal, style: {
            label: 'paypal',
            height: 44,
        }, createVaultSetupToken: handlePaypalInitv2, onApprove: handlePaypalRestSave, onError: () => handlePaypalError() }));
    const paypalRestDisabled = (React.createElement(PayPalButtons, { fundingSource: "paypal", disabled: isSavedPaypal, style: {
            label: 'paypal',
            height: 44,
        }, createOrder: handlePaypalInit, onApprove: handlePaypalSoapSave, onError: () => handlePaypalError() }));
    const getPaypalList = () => {
        var _a;
        const paypalList = ((_a = paypalCards === null || paypalCards === void 0 ? void 0 : paypalCards.paymentCards) === null || _a === void 0 ? void 0 : _a.map((card, cardIndex) => (React.createElement("div", { key: card.paymentId },
            isMobile &&
                alertMessage &&
                !hasDeleteIndex &&
                alertMessage.paymentId === card.paypalAccountId && (React.createElement("div", { className: "sui-mb-8" }, alert)),
            React.createElement(PaypalCard, { key: card.paymentId, card: card, isB2B: isB2B, index: cardIndex, onActionFailure: onActionFailure, onActionSuccess: onActionSuccess, handlePaypalEdit: handlePaypalEdit, handlePaypalError: handlePaypalError }))))) || [];
        if (isMobile && hasDeleteIndex)
            paypalList.splice(deletedCardIndex, 0, React.createElement("div", { className: "-sui-mb-4" }, alert));
        return paypalList;
    };
    useEffect(() => {
        return () => {
            setAlertMessage(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Spinner, { active: loading || isPending }),
        React.createElement("div", { className: "sui-grid sui-grid-cols-1 sui-gap-y-6 md:sui-gap-y-8 md:sui-grid-cols-3 md:sui-gap-x-8 sui-mb-8 md:sui-mb-10" },
            React.createElement("div", { className: "md:sui-col-span-2 sui-order-3 md:sui-order-1" },
                React.createElement(Typography, { weight: "display", variant: "h2", uppercase: true },
                    "PAYPAL (", paginationInfo === null || paginationInfo === void 0 ? void 0 :
                    paginationInfo.totalPayPalAccounts,
                    ")")),
            React.createElement("div", { className: "sui-order-1" },
                !isPending && (React.createElement("div", { className: "sui-mb-2" },
                    React.createElement(Typography, { height: "tight" }, "Add New PayPal Account"))),
                isPaypalRestEnabled ? paypalRestEnabled : paypalRestDisabled),
            isSavedPaypal && (React.createElement("div", { className: "sui-order-2 md:sui-order-1 md:sui-col-span-3" },
                React.createElement("div", { className: "-sui-mb-4" },
                    React.createElement(Alert, { status: "info" }, "You may only have one PayPal account PER USER. To add another, delete your current account first."))))),
        alertMessage &&
            (!isMobile ||
                (alertMessage.paymentId === undefined && !hasDeleteIndex)) && (React.createElement("div", { className: "sui-mb-8" }, alert)),
        hasNoPaymentCards(paypalCards) && (React.createElement(NoPaymentCardsMessage, { tenderType: CardType.PAYPAL_ACCOUNT })),
        React.createElement("div", { className: "sui-grid sui-grid-cols-1 sui-gap-y-8 sui-gap-x-8 md:sui-grid-cols-2 2xl:sui-gap-y-16 2xl:sui-grid-cols-3" }, getPaypalList()),
        totalPages > 1 && (React.createElement("div", { className: "sui-w-full sui-justify-center sui-p-5 sui-flex" },
            React.createElement(Pagination, { nPages: totalPages, onChangePage: (newPage) => {
                    onChangePage(newPage);
                    setAlertMessage(null);
                    scrollToTop();
                }, page: currentPageNumber })))));
};
export const PayPalList = () => (React.createElement(PayPalScriptProvider, { options: { clientId: payPalClientId, vault: true } },
    React.createElement(PayPalListContent, null)));
